import { mailHealth } from './integrations/Connections';
import { fmtDate } from '@jt/core';
import { useEffect } from 'react';
import { Link, matchPath, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useMe, useSettings, useToday, useIsAdmin } from './api/hooks';
import { backgroundOf, useUI } from './components/overlays';
import { Avatar, Icon, type IconName } from './components/ui';
import { Panel } from './panel/Panel';
import { ApplicationsPage } from './pages/Applications';
import { ContactsPage } from './pages/Contacts';
import { InterviewsPage } from './pages/Interviews';
import { MessagesPage } from './pages/Messages';
import { OffersPage } from './pages/Offers';
import { OverviewPage } from './pages/Overview';
import { ResumesPage } from './pages/Resumes';
import { SettingsPage } from './pages/Settings';
import { AdminPage } from './pages/Admin';
import { FeedPage } from './pages/Feed';
import { useIntegrations } from './pages/integrations';
import { APP_VERSION, parseVersion } from './version';

const BUILD = parseVersion(APP_VERSION);

const NAV: [string, string, IconName][] = [
  ['/overview', 'Обзор', 'overview'],
  ['/feed', 'Вакансии', 'search'],
  ['/applications', 'Мои отклики', 'file'],
  ['/messages', 'Сообщения', 'msg'],
  ['/interviews', 'Интервью', 'calendar'],
  ['/contacts', 'Контакты', 'users'],
  ['/resumes', 'Резюме', 'file'],
  ['/offers', 'Сравнение офферов', 'chart'],
];

const MNAV: [string, string, IconName][] = [
  ['/feed', 'Вакансии', 'search'],
  ['/applications', 'Отклики', 'file'],
  ['/messages', 'Сообщения', 'msg'],
  ['/interviews', 'Интервью', 'calendar'],
];

function useFeedNew() {
  return useToday().data?.feedNew ?? 0;
}

function useMessagesBadge() {
  const t = useToday();
  return (t.data?.pendingInbox ?? 0) + (t.data?.needsReply ?? 0);
}

function Sidebar({ current }: { current: string }) {
  const ui = useUI();
  const me = useMe();
  const settings = useSettings();
  const integ = useIntegrations();
  const badge = useMessagesBadge();
  const feedNew = useFeedNew();
  const admin = useIsAdmin();
  const name = settings.data?.name || me.data?.user.name || 'Мой профиль';
  const mail = integ.data?.gmail;
  const tg = integ.data?.telegram;
  return (
    <aside id="side" aria-label="Навигация" className={ui.sideOpen ? 'open' : ''}>
      <Link to="/overview" className="brand" onClick={() => ui.setSideOpen(false)}>
        <span className="bic">
          <Icon name="brief" size={18} />
        </span>
        Поиск работы
      </Link>
      <nav>
        {NAV.map(([to, t, ic]) => (
          <Link
            key={to}
            to={to}
            className="navi"
            aria-current={current === to ? 'page' : undefined}
            onClick={() => ui.setSideOpen(false)}
          >
            <Icon name={ic} size={20} /> {t}
            {to === '/messages' && badge > 0 && <span className="cnt">{badge}</span>}
            {to === '/feed' && feedNew > 0 && <span className="cnt feed">{feedNew}</span>}
          </Link>
        ))}
      </nav>
      <div className="grow" />
      <div className="ver" title={BUILD.commit ? `коммит ${BUILD.commit}` : undefined}>
        version: {BUILD.version}
      </div>
      <Link
        to="/settings"
        className="navi"
        aria-current={current === '/settings' ? 'page' : undefined}
        onClick={() => ui.setSideOpen(false)}
      >
        <Icon name="gear" size={20} /> Настройки
      </Link>
      {admin && (
        <Link
          to="/admin"
          className="navi"
          aria-current={current === '/admin' ? 'page' : undefined}
          onClick={() => ui.setSideOpen(false)}
        >
          <Icon name="users" size={20} /> Пользователи
        </Link>
      )}
      <div className="conn">
        <div>
          <i className={`sdot ${mailHealth(mail)}`} />
          Почта ·{' '}
          {mail?.connected
            ? mail.lastScanAt
              ? `проверено ${fmtDate(mail.lastScanAt.slice(0, 10))}`
              : 'подключена'
            : 'не подключена'}
        </div>
        <div>
          <i className={`sdot ${tg?.linked ? 'ok' : 'off'}`} />
          Telegram · {tg?.linked ? 'привязан' : 'не привязан'}
        </div>
      </div>
      <Link to="/settings#profile" className="me" onClick={() => ui.setSideOpen(false)}>
        <Avatar name={settings.data?.name || me.data?.user.name || 'Я'} size={36} />
        <b>{name}</b>
        <Icon name="chevD" />
      </Link>
    </aside>
  );
}

function MobileNav({ current }: { current: string }) {
  const ui = useUI();
  const badge = useMessagesBadge();
  const feedNew = useFeedNew();
  const more = ['/overview', '/contacts', '/resumes', '/offers', '/settings', '/admin'].includes(current);
  const nav = useNavigate();
  return (
    <nav id="mnav" aria-label="Разделы">
      {MNAV.map(([to, t, ic]) => (
        <button key={to} onClick={() => nav(to)} aria-current={current === to ? 'page' : undefined}>
          <Icon name={ic} size={22} />
          {t}
          {to === '/messages' && badge > 0 && <span className="cnt">{badge}</span>}
          {to === '/feed' && feedNew > 0 && <span className="cnt feed">{feedNew}</span>}
        </button>
      ))}
      <button onClick={() => ui.setSideOpen(true)} aria-current={more ? 'page' : undefined}>
        <Icon name="menu" size={22} />
        Ещё
      </button>
    </nav>
  );
}

export function Shell() {
  const loc = useLocation();
  const ui = useUI();
  const bg = backgroundOf(loc);
  const panelMatch = matchPath('/applications/:id', loc.pathname);
  const panelId = panelMatch?.params.id ?? null;
  const mainLoc = bg ?? (panelId ? { ...loc, pathname: '/applications' } : loc);
  const current = '/' + (mainLoc.pathname.split('/')[1] ?? '');

  useEffect(() => {
    document.body.classList.toggle('panel-open', !!panelId);
    document.body.classList.toggle('side-open', ui.sideOpen);
  }, [panelId, ui.sideOpen]);

  useEffect(() => {
    const titles: Record<string, string> = Object.fromEntries(NAV.map(([to, t]) => [to, t]));
    titles['/settings'] = 'Настройки';
    document.title = `${titles[current] ?? 'Мои отклики'} — Поиск работы`;
  }, [current]);

  return (
    <>
      <a className="skip" href="#main">
        К содержимому
      </a>
      <div className={`app ${panelId ? 'has-panel' : ''}`}>
        <Sidebar current={current} />
        <main id="main" tabIndex={-1}>
          <Routes location={mainLoc}>
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/interviews" element={<InterviewsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/resumes" element={<ResumesPage />} />
            <Route path="/offers" element={<OffersPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="*" element={<ApplicationsPage />} />
          </Routes>
        </main>
        <aside id="panel" aria-label="Карточка вакансии">
          {panelId && <Panel key={panelId} id={panelId} />}
        </aside>
      </div>
      <div id="pscrim" onClick={ui.closePanel} />
      <div id="sscrim" onClick={() => ui.setSideOpen(false)} />
      <MobileNav current={current} />
    </>
  );
}
